/**
 * 지출결의등록(type1) — 연결 묶음 임시세션 (localStorage)
 * 연결(과목1:증빙N) × N → 저장 시 결의번호 1개 채번
 */

import {
  createUid,
  createResolutionDetailUid,
  emptyResolutionDetailRow,
  EXPENSE_STATUS,
  todayYmd,
} from 'expense/expenseStore';
import { buildType1Resolution } from 'expense/resolutionType1Utils';
import {
  isBdApiEnabled,
  POC_BUCKET,
  pocParams,
  loadPocDoc,
  upsertPocDoc,
  enqueuePersist,
} from 'services/pocRowPersist';

export const TYPE1_DRAFT_STORAGE_PREFIX = 'erp.expense.resType1.draft';
/** 결의내역 그리드 결의번호 칸 — 미채번 표시 */
export const TYPE1_DRAFT_NO_LABEL = '(임시저장)';

export function createDraftSessionUid() {
  return createUid('DRF');
}

export function createBundleUid() {
  return createUid('BND');
}

function draftStorageKey(dims = {}, userKey = '') {
  return [
    TYPE1_DRAFT_STORAGE_PREFIX,
    String(dims.tenantCode || '').trim(),
    String(dims.fiscalYear || '').trim(),
    String(userKey || '').trim(),
  ].join('|');
}

export function projectAxisKey(project = {}) {
  return [
    String(project.deptCode || '').trim(),
    String(project.projectCode || '').trim(),
    String(project.fundCode || '').trim(),
  ].join('|');
}

export function emptyDraftSession(partial = {}) {
  return {
    draftSessionUid: createDraftSessionUid(),
    fiscalYear: '',
    tenantCode: '',
    project: null,
    bundles: [],
    updatedAt: '',
    ...partial,
  };
}

const draftMem = new Map();

function draftMemKey(dims, userKey) {
  return draftStorageKey(dims, userKey);
}

export function loadType1Draft(dims, userKey) {
  const key = draftMemKey(dims, userKey);
  if (draftMem.has(key)) return draftMem.get(key);
  try {
    const raw = localStorage.getItem(draftStorageKey(dims, userKey));
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data?.draftSessionUid) return null;
    const session = {
      ...emptyDraftSession(),
      ...data,
      bundles: Array.isArray(data.bundles) ? data.bundles : [],
    };
    draftMem.set(key, session);
    return session;
  } catch {
    return null;
  }
}

export async function fetchType1Draft(dims, userKey) {
  const key = draftMemKey(dims, userKey);
  if (isBdApiEnabled()) {
    const doc = await loadPocDoc({
      bucket: POC_BUCKET.TYPE1_DRAFT,
      rowUid: key,
      params: { ...pocParams(dims), userKey },
    });
    const session = doc?.draftSessionUid
      ? { ...emptyDraftSession(), ...doc, bundles: Array.isArray(doc.bundles) ? doc.bundles : [] }
      : null;
    draftMem.set(key, session);
    return session;
  }
  return loadType1Draft(dims, userKey);
}

export function saveType1Draft(dims, userKey, session) {
  const key = draftMemKey(dims, userKey);
  if (!session?.draftSessionUid) {
    draftMem.set(key, null);
    localStorage.removeItem(draftStorageKey(dims, userKey));
    if (isBdApiEnabled()) {
      enqueuePersist(key, () =>
        upsertPocDoc({
          bucket: POC_BUCKET.TYPE1_DRAFT,
          rowUid: key,
          payload: null,
          params: { ...pocParams(dims), userKey },
        })
      );
    }
    return null;
  }
  const next = {
    ...session,
    updatedAt: new Date().toISOString(),
  };
  draftMem.set(key, next);
  localStorage.setItem(draftStorageKey(dims, userKey), JSON.stringify(next));
  if (isBdApiEnabled()) {
    enqueuePersist(key, () =>
      upsertPocDoc({
        bucket: POC_BUCKET.TYPE1_DRAFT,
        rowUid: key,
        payload: next,
        params: { ...pocParams(dims), userKey },
        userKey,
      })
    );
  }
  return next;
}

export function clearType1Draft(dims, userKey) {
  localStorage.removeItem(draftStorageKey(dims, userKey));
}

export function isType1DraftRow(row) {
  return !!(row?.isDraft || row?.resolutionNo === TYPE1_DRAFT_NO_LABEL);
}

/** 채번 완료 여부 — 승인요청·GW 대상 판별 */
export function hasIssuedResolutionNo(row) {
  if (!row || isType1DraftRow(row)) return false;
  const no = String(row.resolutionNo || '').trim();
  if (!no || no === TYPE1_DRAFT_NO_LABEL) return false;
  return true;
}

export function summarizeDraftSession(session) {
  const bundles = (session?.bundles || []).filter((b) => b && b.mod !== 'D');
  const payees = bundles.flatMap((b) => (b.payees || []).filter((p) => p.mod !== 'D'));
  const subjectLines = bundles.map((b) => ({
    subjectCode: b.subjectCode || '',
    subjectName: b.subjectName || '',
    balanceUid: b.balanceUid || '',
    fundCode: b.fundCode || '',
    fundName: b.fundName || '',
    balanceAmt: Number(b.balanceAmt) || 0,
    availableAmt: Number(b.availableAmt) || 0,
    applyAmt: Number(b.applyAmt) || 0,
  }));
  const resolutionAmt = payees.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const primary = subjectLines[0] || {};
  return {
    bundles,
    payees,
    subjectLines,
    resolutionAmt,
    subjectCount: subjectLines.length,
    subjectSummary:
      subjectLines.length > 1
        ? `${primary.subjectCode || ''} 외 ${subjectLines.length - 1}`
        : primary.subjectCode || '',
    evidenceCount: payees.length,
  };
}

/** 결의내역 그리드용 임시행 */
export function draftSessionToListRow(session) {
  if (!session?.draftSessionUid || !(session.bundles || []).length) return null;
  const sum = summarizeDraftSession(session);
  const p = session.project || {};
  return {
    id: session.draftSessionUid,
    resolutionUid: session.draftSessionUid,
    draftSessionUid: session.draftSessionUid,
    isDraft: true,
    resolutionNo: TYPE1_DRAFT_NO_LABEL,
    resolutionDate: todayYmd(),
    deptCode: p.deptCode || '',
    deptName: p.deptName || '',
    projectCode: p.projectCode || '',
    projectName: p.projectName || '',
    fundCode: p.fundCode || '',
    fundName: p.fundName || '',
    subjectCode: sum.subjectLines[0]?.subjectCode || '',
    subjectName: sum.subjectSummary,
    subjectSummary: sum.subjectSummary,
    subjectCount: sum.subjectCount,
    resolutionAmt: sum.resolutionAmt,
    status: EXPENSE_STATUS.DRAFT,
    statusNm: '임시저장',
    remark: `연결 ${sum.bundles.length}묶음 · 증빙/채주 ${sum.evidenceCount}건`,
    gwLinked: '',
    registerLayout: 'TYPE1',
    subjectLines: sum.subjectLines,
    mod: '',
  };
}

/**
 * 연결 1회 — 과목 1 + 채주 N → 묶음 추가
 * @returns {{ ok: boolean, session?: object, error?: string }}
 */
export function linkBundleToDraft(session, { project, subject, payees }) {
  const projCode = String(project?.projectCode || '').trim();
  const subjectCode = String(subject?.subjectCode || '').trim();
  const list = (payees || []).filter((p) => p && p.mod !== 'D');
  if (!projCode) return { ok: false, error: '사업을 선택하세요.' };
  if (!subjectCode) return { ok: false, error: '과목을 1개 선택하세요.' };
  if (!list.length) return { ok: false, error: '채주(증빙)를 1건 이상 선택하세요.' };

  for (const p of list) {
    if (!String(p.vendorName || '').trim()) {
      return { ok: false, error: '채주명을 입력하세요.' };
    }
    if (!(Number(p.amount) > 0)) {
      return { ok: false, error: `「${p.vendorName || '채주'}」의 금액을 입력하세요.` };
    }
  }

  let next = session;
  if (!next?.draftSessionUid) {
    next = emptyDraftSession({
      fiscalYear: project.fiscalYear || '',
      tenantCode: project.tenantCode || '',
      project: {
        deptCode: project.deptCode || '',
        deptName: project.deptName || '',
        projectCode: projCode,
        projectName: project.projectName || '',
        fundCode: project.fundCode || '',
        fundName: project.fundName || '',
      },
      bundles: [],
    });
  } else {
    const axis = projectAxisKey(next.project);
    const nextAxis = projectAxisKey(project);
    if (axis && nextAxis && axis !== nextAxis) {
      return {
        ok: false,
        error: `임시결의는 사업 1개만 가능합니다. 현재 ${next.project?.projectCode} 에 연결되어 있습니다.`,
      };
    }
  }

  const existingEv = new Set(
    (next.bundles || []).flatMap((b) =>
      (b.payees || []).map((p) => p.evidenceUid || p.statementUid || p.detailUid).filter(Boolean)
    )
  );
  for (const p of list) {
    const key = p.evidenceUid || p.statementUid || p.detailUid;
    if (key && existingEv.has(key)) {
      return { ok: false, error: '이미 이 임시결의에 연결된 채주/증빙이 포함되어 있습니다.' };
    }
  }

  const applyAmt = list.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const bundle = {
    bundleUid: createBundleUid(),
    subjectCode,
    subjectName: subject.subjectName || '',
    balanceUid: subject.balanceUid || '',
    fundCode: subject.fundCode || project.fundCode || '',
    fundName: subject.fundName || project.fundName || '',
    balanceAmt: Number(subject.balanceAmt) || 0,
    availableAmt: Number(subject.availableAmt) || 0,
    applyAmt,
    payees: list.map((p) =>
      emptyResolutionDetailRow({
        ...p,
        detailUid: p.detailUid || createResolutionDetailUid(),
        subjectCode,
        subjectName: subject.subjectName || '',
        mod: 'A',
      })
    ),
    linkedAt: new Date().toISOString(),
    mod: '',
  };

  return {
    ok: true,
    session: {
      ...next,
      project: next.project || {
        deptCode: project.deptCode || '',
        deptName: project.deptName || '',
        projectCode: projCode,
        projectName: project.projectName || '',
        fundCode: project.fundCode || '',
        fundName: project.fundName || '',
      },
      bundles: [...(next.bundles || []), bundle],
    },
  };
}

/** 임시세션 → 결의 초안 (채번 직전) */
export function buildResolutionFromDraft(session, fiscalYear) {
  const sum = summarizeDraftSession(session);
  if (!sum.bundles.length) return null;
  return buildType1Resolution({
    fiscalYear,
    projectRow: session.project || {},
    subjectRows: sum.subjectLines,
    payeeRows: sum.payees,
    resolutionUid: '', // 채번 시 새 UID
    remark: `type1 연결 ${sum.bundles.length}묶음`,
  });
}
